import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, Drawer, Input, InputNumber, Popconfirm, Radio, Space, Table, Tooltip, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { errorMessage } from './useOperators';
import { IPlan, OPTIONAL_MODULES, OptionalModule } from '../../../types/models/tenancy';

export interface PlansDrawerProps {
  visible: boolean;
  onClose: () => void;
  plans: IPlan[];
  save: (plan: Partial<IPlan>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

const slugify = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Edit the plans: which modules each starts a tenant with, how long its trial
 * runs, and which one a new trial gets. One row at a time, saved explicitly.
 * Editing a plan never changes tenants already on it; their switches are theirs.
 */
const PlansDrawer = ({ visible, onClose, plans, save, remove }: PlansDrawerProps) => {
  const [rows, setRows] = useState<Partial<IPlan>[]>(plans);
  const [savingId, setSavingId] = useState<number | 'new'>();

  useEffect(() => { setRows(plans); }, [plans]);

  const update = (index: number, patch: Partial<IPlan>) =>
    setRows(previous => previous.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const isDirty = (row: Partial<IPlan>) => {
    const savedRow = plans.find(p => p.id === row.id);
    return !savedRow || JSON.stringify(savedRow) !== JSON.stringify(row);
  };

  const submit = async (row: Partial<IPlan>) => {
    setSavingId(row.id ?? 'new');
    try {
      await save({ ...row, key: row.key || slugify(row.name || '') });
      message.success(`${row.name} saved.`);
    } catch (e: any) {
      message.error(errorMessage(e, 'Could not save this plan.'));
    } finally {
      setSavingId(undefined);
    }
  };

  const columns = [
    {
      title: 'Plan',
      width: 160,
      render: (_: any, row: Partial<IPlan>, index: number) => (
        <Input value={row.name} onChange={e => update(index, { name: e.target.value })} placeholder="Name" />
      ),
    },
    {
      title: 'Modules it starts with',
      render: (_: any, row: Partial<IPlan>, index: number) => (
        <Checkbox.Group
          value={row.modules}
          onChange={modules => update(index, { modules: modules as OptionalModule[] })}
          options={OPTIONAL_MODULES.map(m => ({ value: m.key, label: m.label }))}
        />
      ),
    },
    {
      title: 'Trial days',
      width: 100,
      render: (_: any, row: Partial<IPlan>, index: number) => (
        <InputNumber min={1} max={365} value={row.trial_days}
          onChange={days => update(index, { trial_days: Number(days) || 30 })} />
      ),
    },
    {
      title: <Tooltip title="The plan a new trial gets when none is chosen.">New trials</Tooltip>,
      width: 90,
      align: 'center' as const,
      render: (_: any, row: Partial<IPlan>, index: number) => (
        <Radio checked={!!row.is_trial_default}
          onChange={() => setRows(previous => previous.map((r, i) => ({ ...r, is_trial_default: i === index })))} />
      ),
    },
    {
      title: 'Tenants',
      width: 70,
      align: 'center' as const,
      render: (_: any, row: Partial<IPlan>) => row.operator_count ?? 0,
    },
    {
      width: 130,
      render: (_: any, row: Partial<IPlan>) => (
        <Space>
          <Button size="small" type="primary" disabled={!isDirty(row) || !row.name?.trim()}
            loading={savingId === (row.id ?? 'new')} onClick={() => submit(row)}>
            Save
          </Button>
          {row.id ? (
            <Popconfirm
              title={row.operator_count ? `${row.operator_count} tenant(s) are on it.` : `Delete ${row.name}?`}
              disabled={!!row.operator_count}
              onConfirm={() => remove(row.id!).catch(e => message.error(errorMessage(e, 'Could not delete.')))}
            >
              <Tooltip title={row.operator_count ? 'In use: move its tenants first' : undefined}>
                <Button size="small" icon={<DeleteOutlined />} disabled={!!row.operator_count} />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Button size="small" onClick={() => setRows(plans)}>Cancel</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Drawer title="Plans" width={1000} visible={visible} onClose={onClose}>
      <p style={{ marginTop: 0 }}>
        A plan is the set of modules a tenant starts with. Each tenant can still switch any module on
        or off for a custom deal, and editing a plan never changes tenants already on it. Prices live
        with billing, not here.
      </p>
      <Table
        rowKey={row => String(row.id ?? 'new')}
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
      />
      {!rows.some(row => !row.id) && (
        <Button
          icon={<PlusOutlined />}
          style={{ marginTop: 12 }}
          onClick={() => setRows([...rows, {
            name: '', modules: [], trial_days: 30, is_trial_default: false, position: rows.length,
          }])}
        >
          Add plan
        </Button>
      )}
    </Drawer>
  );
};

export default PlansDrawer;
